import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, ChevronDown, Mail } from 'lucide-react';

const FAQ_SECTIONS = [
  {
    title: 'General',
    items: [
      { q: 'What is BitBot?', a: 'BitBot is an automated cryptocurrency trading bot that connects to your Coinbase account. It uses technical indicators like RSI (Relative Strength Index) to automatically buy when coins are oversold and sell when they\'re overbought, 24/7.' },
      { q: 'Is BitBot free?', a: 'BitBot offers both free simulation mode (paper trading with virtual funds) and paid subscription tiers for live trading with real money through your Coinbase account.' },
      { q: 'Do I need to know how to trade?', a: 'No! BitBot handles the trading decisions automatically. Start with simulation mode to see how it works risk-free. The AI Assistant can also answer any questions about trading strategies.' },
      { q: 'What coins does BitBot support?', a: 'BitBot supports BTC, ETH, SOL, LTC, DOGE, BCH, XRP, and SHIB. You can enable or disable individual coins in your configuration.' },
    ],
  },
  {
    title: 'Trading & Strategy',
    items: [
      { q: 'How does the RSI strategy work?', a: 'RSI measures whether a coin is oversold (price has dropped a lot) or overbought (price has risen a lot). BitBot buys when RSI falls below your oversold threshold (default 30) and sells when it rises above your overbought threshold (default 70). This is a classic mean-reversion strategy.' },
      { q: 'What is per-lot profit checking?', a: 'Instead of averaging all your holdings, BitBot evaluates each individual buy "lot" separately. A lot is only sold if its specific profit (after estimated exchange fees) meets your minimum threshold. This prevents selling profitable lots to subsidize losing ones.' },
      { q: 'What are protection sells?', a: 'Stop-loss sells if price drops too much (limits your downside). Take-profit sells if price rises significantly (locks in gains). Trailing stop follows the price up and sells if it reverses. All are configurable in your settings.' },
      { q: 'How are fees handled?', a: 'Coinbase charges ~1.2% per trade (taker fee at base tier). BitBot estimates the full round-trip cost (buy + sell fees, ~2.5% with buffer) and only sells a lot if the profit exceeds total fees. This prevents "profitable" trades that actually lose money after fees.' },
      { q: 'What is Lot Health?', a: 'Lot Health shows how close each of your buy lots is to being profitably sellable. 0% means the lot is at its buy price, 100% means it\'s reached the minimum sell target (including fees). Green = ready to sell, Red = underwater.' },
    ],
  },
  {
    title: 'Live Trading',
    items: [
      { q: 'How do I connect my Coinbase account?', a: 'Go to Config > Exchange Settings and enter your Coinbase Advanced Trade API key and secret. We recommend creating a key with trade-only permissions (no withdrawal access) for security.' },
      { q: 'Is my money safe?', a: 'Your funds stay in your Coinbase account at all times. BitBot only has trade permissions — it cannot withdraw your funds. You can revoke API access anytime from your Coinbase settings. However, trading cryptocurrency carries inherent financial risk.' },
      { q: 'What happens if the bot stops?', a: 'Your existing positions remain in your Coinbase account. No new trades execute until the bot is restarted. You\'ll receive a notification (email/Telegram) when the bot stops, with the specific reason.' },
      { q: 'Can I trade manually while the bot is running?', a: 'Yes. The bot syncs with your exchange and accounts for manual trades. However, manual trades may affect the bot\'s lot tracking and cost basis calculations.' },
    ],
  },
  {
    title: 'Simulation Mode',
    items: [
      { q: 'What is simulation mode?', a: 'Simulation mode lets you test BitBot with virtual funds ($1,000 starting balance). Trades are simulated using real market prices but no actual money is involved. It\'s the best way to learn how the bot works and test different strategies.' },
      { q: 'Does simulation performance predict live results?', a: 'Not exactly. Simulation doesn\'t account for real exchange fees, slippage, order fill delays, or liquidity issues. Live trading results will differ. Use simulation to understand the strategy, not as a guarantee of returns.' },
    ],
  },
  {
    title: 'Notifications & Monitoring',
    items: [
      { q: 'What notifications can I receive?', a: 'Trade alerts (every buy/sell), bot start/stop events, daily P/L summaries, and new badge achievements. These can be sent via email, Telegram, or browser push notifications.' },
      { q: 'How do I set up Telegram alerts?', a: 'Go to Config > Telegram Settings. Create a bot via @BotFather on Telegram, paste the bot token, and your chat ID. Use the "Test" button to verify it works.' },
    ],
  },
  {
    title: 'Account & Security',
    items: [
      { q: 'How do I sign up?', a: 'BitBot uses Google OAuth for authentication. Click "Sign in with Google" on the login page. No separate password needed.' },
      { q: 'Can I delete my account?', a: 'Yes. Contact us at eviltim@gmail.com to request account deletion. We will remove all your data including trade history, settings, and API credentials.' },
      { q: 'Is my data encrypted?', a: 'Yes. All connections use HTTPS encryption. Exchange API keys are stored encrypted at rest. Session tokens use secure HTTP-only cookies.' },
    ],
  },
];

const FaqItem = ({ q, a }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-b border-[#003B00]/30">
      <button onClick={() => setOpen(!open)} className="w-full flex items-center justify-between py-3 text-left">
        <span className="text-sm text-white font-medium pr-4">{q}</span>
        <ChevronDown className={`h-4 w-4 text-gray-500 shrink-0 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <p className="text-sm text-gray-400 leading-relaxed pb-3 pr-8">{a}</p>
      )}
    </div>
  );
};

export default function FaqPage() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-black text-white">
      <div className="max-w-3xl mx-auto px-4 py-8">
        <button onClick={() => navigate('/dashboard')} className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-[#00FF41] transition-colors mb-8">
          <ArrowLeft className="h-4 w-4" /> Back to Dashboard
        </button>

        <h1 className="text-4xl font-bold mb-2">
          <span className="text-white">Frequently Asked </span>
          <span className="text-[#00FF41]">Questions</span>
        </h1>
        <p className="text-gray-400 mb-8">Everything you need to know about BitBot and automated crypto trading.</p>

        <div className="space-y-8">
          {FAQ_SECTIONS.map((section) => (
            <div key={section.title}>
              <h2 className="text-lg font-bold text-[#00FF41] mb-3">{section.title}</h2>
              <div className="border border-[#003B00]/30 bg-[#050505] divide-y divide-[#003B00]/30 px-4">
                {section.items.map((item) => (
                  <FaqItem key={item.q} q={item.q} a={item.a} />
                ))}
              </div>
            </div>
          ))}
        </div>

        <div className="mt-12 border border-[#003B00] bg-[#050505] p-6 text-center">
          <h3 className="text-lg font-bold text-white mb-2">Still Have Questions?</h3>
          <p className="text-sm text-gray-400 mb-4">Can't find what you're looking for? Reach out directly or ask the AI Assistant.</p>
          <div className="flex items-center justify-center gap-4">
            <a href="mailto:eviltim@gmail.com" className="inline-flex items-center gap-2 text-sm text-[#00FF41] hover:text-white border border-[#003B00] px-4 py-2 hover:bg-[#003B00] transition-colors">
              <Mail className="h-4 w-4" /> Email Us
            </a>
            <a href="/about" className="text-sm text-gray-400 hover:text-[#00FF41] border border-[#003B00]/50 px-4 py-2 hover:bg-[#003B00]/20 transition-colors">
              About Us
            </a>
          </div>
        </div>

        <footer className="mt-12 pt-6 border-t border-[#003B00]/30 flex flex-wrap gap-4 text-xs text-gray-600">
          <a href="/about" className="hover:text-[#00FF41]">About</a>
          <a href="/privacy" className="hover:text-[#00FF41]">Privacy Policy</a>
          <a href="/terms" className="hover:text-[#00FF41]">Terms of Service</a>
          <a href="/changelog" className="hover:text-[#00FF41]">Changelog</a>
        </footer>
      </div>
    </div>
  );
}
